import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  TablePagination,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  RestartAlt as RestartAltIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { handleEnterAsTabNavigation } from '../../utils/enterAsTab';
import api from '../../services/api';
import facturaCompraService, {
  FacturaCompraFormularioDto,
  CreateItemCompraDto,
  FacturaCompra
} from '../../services/facturaCompraService';
import notaCreditoCompraService, { NotaCreditoCompraCreatePayload } from '../../services/notaCreditoCompraService';
import CompraPendienteHiloInfoDialog from '../../components/compras/CompraPendienteHiloInfoDialog';
import ItemNotasTextField from '../../components/ItemNotasTextField';
import SearchableSelect from '../../components/SearchableSelect';
import ProveedorSelectorField from '../../components/ProveedorSelectorField';
import ObservacionesConNotaProveedor from '../../components/ObservacionesConNotaProveedor';
import UbicacionSearch from '../../components/UbicacionSearch';
import { useAutoUbicacionPredeterminada } from '../../hooks/useAutoUbicacionPredeterminada';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { useSucursalAltaFormulario } from '../../hooks/useSucursalAltaFormulario';
import { persistGlobalNroSuc } from '../../hooks/useSucursalesVentaFiltro';
import ImputableSearch from '../../components/ImputableSearch';
import productoService from '../../services/productoService';
import {
  cargarProductoParaDocumento,
  clearRechazoDiscontinuado,
  esDiscontinuadoBloqueadoParaAgregar,
  esTipoDiscontinuado,
  evaluarUsoProductoDocumento,
  mensajeArticuloDiscontinuadoBloqueado,
  paramsBusquedaProductoDocumento,
  type ModoBloqueoArtDiscontinuados,
} from '../../utils/bloqueaArtDiscontinuados';
import impuestoService from '../../services/impuestoService';
import variablesSistemaService from '../../services/variablesSistemaService';
import comprasSeguimientoService, {
  CompraPendienteMaster,
  cantidadFacturadaNetaDisponible,
  cantidadSugeridaDesdeFacturadoCompra,
  cantidadSugeridaDesdePendiente,
  costoBrutoDesdePendienteMaster,
  formatComprobanteMasterDisplay,
  formatFechaEmisionComprobanteMaster,
  porcDescDesdePendienteMaster,
} from '../../services/comprasSeguimientoService';
import { Imputable } from '../../services/imputableService';
import { IUbicacion } from '../../interfaces/Ubicacion';
import proveedorService, { Proveedor } from '../../services/proveedorService';
import { formaDePagoDesdeCondVtaProveedor } from '../../utils/formaPagoCompra';
import { formatCurrency, formatDateLocal, toLocalDateStringYMD } from '../../utils/formatters';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import { getApiErrorMessage } from '../../utils/apiError';

interface Compania {
  idCompania: string;
  nombre: string;
  condIva?: string;
}

const FORMAS_DE_PAGO = [
  'CONTADO',
  'CTA.CTE.',
  'EFECTIVO',
  'DEVOLUCION',
  'CUOTAS',
  'CONTRAREEMBO',
  'PAGOANTICIPA'
];

/** Límite items.ite_notas */
const ITEM_NOTAS_MAX = 500;

const getFirstDayOfMonthYmd = (): string => {
  const d = new Date();
  d.setDate(1);
  return toLocalDateStringYMD(d);
};

const getLastDayOfMonthYmd = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return toLocalDateStringYMD(d);
};

const formatoFacturaCompraCorto = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

function normalizeItemNotas(s?: string | null): string | undefined {
  const t = (s ?? '').trim();
  return t ? t.slice(0, ITEM_NOTAS_MAX) : undefined;
}

/** Encabezado de NC: igual a factura de compra + factura origen opcional */
type NcFormulario = FacturaCompraFormularioDto & { nroFormularioFactura?: number };

const defaultFormulario: NcFormulario = {
  idCompania: '',
  letra: '',
  nroSuc: 0,
  nroComprobante: 0,
  tipoProveedor: '',
  tipoComprobante: '',
  descripcion: '',
  fechaEmision: toLocalDateStringYMD(),
  fechaVencim: toLocalDateStringYMD(),
  aplicacionIva: toLocalDateStringYMD(),
  nroProveedor: 0,
  formaDePago: 'CONTADO',
  nroUbicacionDestino: undefined,
  porcDesc: 0,
  porcDesc2: 0,
  porcDesc3: 0,
  nroFormularioFactura: undefined
};

interface ImpuestoAdicionalRow {
  codigoImpuesto: string;
  ctaCtable: number;
  descripcion: string;
  montoNeto: number;
  montoImpuesto: number;
  valor?: number;
  basadoen?: number;
  montoGravMin?: number;
  montoGravMax?: number;
  montoImpuestoMin?: number;
  montoImpuestoMax?: number;
  ctaCtableLabel?: string;
}

const SUBTOTAL_RESET_KEYS = [
  'cantidad',
  'costoBrutoUnitario',
  'porcDesc1',
  'porcDesc2',
  'porcDesc3',
  'porcDesc4',
  'porcDesc5',
] as const;

const defaultItem: CreateItemCompraDto = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  costoBrutoUnitario: 0,
  porcDesc1: 0,
  porcDesc2: 0,
  porcDesc3: 0,
  porcDesc4: 0,
  porcDesc5: 0,
  notas: '',
  tipoProveedor: 'C'
};

const defaultItemConcepto: CreateItemCompraDto = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  costoBrutoUnitario: 0,
  porcDesc1: 0,
  porcDesc2: 0,
  porcDesc3: 0,
  porcDesc4: 0,
  porcDesc5: 0,
  notas: '',
  tipoProveedor: 'G'
};

const DecimalTextField: React.FC<{
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
  sx?: any;
  inputProps?: any;
}> = ({ value, onChange, placeholder, sx, inputProps }) => {
  const [localValue, setLocalValue] = useState<string>(String(value ?? 0));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setLocalValue(String(value ?? 0));
  }, [value, focused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = String(e.target.value ?? '');
    // Permitimos solo dígitos y separadores decimales
    raw = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
    // Evitar más de un '.'
    const parts = raw.split('.');
    if (parts.length > 2) raw = `${parts[0]}.${parts.slice(1).join('')}`;

    setLocalValue(raw);

    // No actualizamos el state numérico si el valor está incompleto
    if (raw === '' || raw === '.' || raw.endsWith('.')) return;
    const n = Number(raw);
    if (!Number.isNaN(n) && Number.isFinite(n)) onChange(n);
  };

  const handleBlur = () => {
    setFocused(false);
    const currentV = Number(value ?? 0);
    const raw = String(localValue ?? '').trim();
    // Si el usuario dejó un '.' final (ej: "25.") lo interpretamos como "25"
    const normalized = raw.endsWith('.') ? raw.slice(0, -1) : raw;
    if (normalized === '' || normalized === '.') {
      setLocalValue('0');
      if (currentV !== 0) onChange(0);
      return;
    }
    const n = Number(normalized);
    const finalV = Number.isNaN(n) ? 0 : n;
    setLocalValue(String(finalV));
    if (finalV !== currentV) onChange(finalV);
  };

  return (
    <TextField
      size="small"
      type="text"
      value={localValue}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      placeholder={placeholder}
      sx={sx}
      inputProps={{
        inputMode: 'decimal',
        ...inputProps
      }}
    />
  );
};

const formatPesosInput = (value: number): string => {
  const safe = Number.isFinite(value) ? value : 0;
  return safe.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const parsePesosInput = (raw: string): number => {
  const cleaned = (raw ?? '').replace(/\s/g, '').replace(/\$/g, '');
  if (!cleaned) return 0;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  let normalized = cleaned;

  if (hasComma && hasDot) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    normalized = cleaned.replace(',', '.');
  }

  normalized = normalized.replace(/[^0-9.]/g, '');
  const parts = normalized.split('.');
  if (parts.length > 2) {
    normalized = `${parts[0]}.${parts.slice(1).join('')}`;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const CurrencyTextField: React.FC<{
  value: number;
  onChange: (v: number) => void;
  label?: string;
  fullWidth?: boolean;
  sx?: any;
}> = ({ value, onChange, label, fullWidth, sx }) => {
  const focusedRef = useRef(false);
  /** Valor que acabamos de confirmar; ignora syncs del padre con el monto anterior. */
  const pendingRef = useRef<number | null>(null);
  const [localValue, setLocalValue] = useState<string>(formatPesosInput(value ?? 0));

  useEffect(() => {
    if (focusedRef.current) return;
    if (pendingRef.current != null) {
      if (Math.abs((value ?? 0) - pendingRef.current) < 0.005) {
        pendingRef.current = null;
        setLocalValue(formatPesosInput(value ?? 0));
      }
      return;
    }
    setLocalValue(formatPesosInput(value ?? 0));
  }, [value]);

  return (
    <TextField
      size="small"
      type="text"
      label={label}
      fullWidth={fullWidth}
      value={localValue}
      onFocus={() => {
        focusedRef.current = true;
        pendingRef.current = null;
        setLocalValue(formatPesosInput(value ?? 0));
      }}
      onChange={(e) => {
        const raw = String(e.target.value ?? '');
        const sanitized = raw.replace(/[^0-9.,]/g, '');
        setLocalValue(sanitized);
        if (sanitized === '' || sanitized.endsWith(',') || sanitized.endsWith('.')) return;
        const parsed = parsePesosInput(sanitized);
        pendingRef.current = parsed;
        onChange(parsed);
      }}
      onBlur={() => {
        const parsed = parsePesosInput(localValue);
        const formatted = formatPesosInput(parsed);
        setLocalValue(formatted);
        pendingRef.current = parsed;
        onChange(parsed);
        focusedRef.current = false;
      }}
      sx={sx}
      inputProps={{ inputMode: 'decimal' }}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>
      }}
    />
  );
};

const round2Nc = (n: number) => Math.round(n * 100) / 100;

/** Ajusta costo unitario de cada ítem para que el subtotal bruto sume el objetivo.
 * La NC de compra no aplica `subtotalManual` de ítem: el API recalcula qty × costo. */
function aplicarSubtotalManualEnItems(
  items: CreateItemCompraDto[],
  targetNetoTrasBonif: number,
  factorBonif: number,
  calcSubtotal: (it: CreateItemCompraDto) => number,
): CreateItemCompraDto[] {
  if (items.length === 0) return items;
  const targetBruto =
    factorBonif > 0 ? round2Nc(targetNetoTrasBonif / factorBonif) : round2Nc(targetNetoTrasBonif);
  const brutos = items.map((it) => calcSubtotal(it));
  const sumBruto = round2Nc(brutos.reduce((a, b) => a + b, 0));

  return items.map((it, i) => {
    const newSub =
      sumBruto > 0
        ? round2Nc(brutos[i] * (targetBruto / sumBruto))
        : i === 0
          ? targetBruto
          : 0;
    const qty = Number(it.cantidad) || 1;
    const costo = qty > 0 ? round2Nc(newSub / qty) : newSub;
    return {
      ...it,
      // Sin desc. de ítem: el subtotal queda = cantidad × costo.
      costoBrutoUnitario: costo > 0 ? costo : it.costoBrutoUnitario,
      porcDesc1: 0,
      porcDesc2: 0,
      porcDesc3: 0,
      porcDesc4: 0,
      porcDesc5: 0,
      subtotalManual: undefined,
    };
  });
}

/** Coloca el IVA manual en tasa1 del primer ítem (el API arma el total desde tasas de ítems). */
function aplicarIvaManualEnItems(
  items: CreateItemCompraDto[],
  ivaTotal: number,
): CreateItemCompraDto[] {
  if (items.length === 0) return items;
  const iva = round2Nc(ivaTotal);
  return items.map((it, i) => ({
    ...it,
    tasa1: i === 0 ? iva : 0,
    tasa2: 0,
    tasa3: 0,
  }));
}

const NotaCreditoCompraForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [formulario, setFormulario] = useState<NcFormulario>({ ...defaultFormulario });
  const { sucursales, loading: loadingSucursales } = useSucursalesUsuarioContext();
  const setNroSucFormulario = useCallback((nroSuc: number) => {
    setFormulario((prev) => (Number(prev.nroSuc) === nroSuc ? prev : { ...prev, nroSuc }));
  }, []);
  const { onSucursalChange } = useSucursalAltaFormulario({
    nroSuc: Number(formulario.nroSuc) || 0,
    setNroSuc: setNroSucFormulario,
    sucursales,
    loadingSucursales,
  });
  useEffect(() => {
    const v = Number(formulario.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      persistGlobalNroSuc(v);
    }
  }, [formulario.nroSuc]);
  const [itemsProductos, setItemsProductos] = useState<CreateItemCompraDto[]>([]);
  const [itemsConceptos, setItemsConceptos] = useState<CreateItemCompraDto[]>([]);
  /** Si no es null, reemplaza el IVA total del resumen y se envía como `ivaManual` al API. */
  const [ivaManualUsuario, setIvaManualUsuario] = useState<number | null>(null);
  /** Si no es null, reemplaza Subtotal Gravado/Neto del resumen y se envía como `subtotalManual` al API. */
  const [subtotalManualUsuario, setSubtotalManualUsuario] = useState<number | null>(null);
  const [loadingFacturaOrigen, setLoadingFacturaOrigen] = useState(false);
  /** Etiqueta legible de la factura origen vinculada (letra-suc-nro). */
  const [facturaOrigenLabel, setFacturaOrigenLabel] = useState('');
  const [openBuscarFacturaModal, setOpenBuscarFacturaModal] = useState(false);
  const [facturasModalLoading, setFacturasModalLoading] = useState(false);
  const [facturasModalList, setFacturasModalList] = useState<FacturaCompra[]>([]);
  const [facturasModalTotal, setFacturasModalTotal] = useState(0);
  const [facturasModalPage, setFacturasModalPage] = useState(0);
  const [facturasModalSearch, setFacturasModalSearch] = useState('');
  const [facturasModalFechaDesde, setFacturasModalFechaDesde] = useState(getFirstDayOfMonthYmd);
  const [facturasModalFechaHasta, setFacturasModalFechaHasta] = useState(getLastDayOfMonthYmd);
  const [facturasModalQueryKey, setFacturasModalQueryKey] = useState(0);
  const facturasModalRowsPerPage = 10;
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [proveedorNombre, setProveedorNombre] = useState('');
  const [proveedorCuit, setProveedorCuit] = useState('');
  const [proveedorTipoIva, setProveedorTipoIva] = useState('');
  const [ubicacionNombre, setUbicacionNombre] = useState('');
  const [openUbicacionSearch, setOpenUbicacionSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openProductSearch, setOpenProductSearch] = useState(false);
  const [productSearchIndex, setProductSearchIndex] = useState<number | null>(null);
  const [productos, setProductos] = useState<any[]>([]);
  const [modoBloqueoDis, setModoBloqueoDis] = useState<ModoBloqueoArtDiscontinuados>(1);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productosPage, setProductosPage] = useState(0);
  const [productosRowsPerPage] = useState(10);
  const [productosTotalCount, setProductosTotalCount] = useState(0);
  const [openPendientes, setOpenPendientes] = useState(false);
  const [pendientes, setPendientes] = useState<CompraPendienteMaster[]>([]);
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [productCache, setProductCache] = useState<Record<string, {
    calculaIva?: string;
    ivaRI?: number;
    ivaCF?: number;
    ivaMT?: number;
    ivaRN?: number;
    nroTasaIva?: number;
  }>>({});
  const [impuestosAdicionales, setImpuestosAdicionales] = useState<ImpuestoAdicionalRow[]>([]);
  const [openImpuestoSearch, setOpenImpuestoSearch] = useState(false);
  const [impuestoSearchTerm, setImpuestoSearchTerm] = useState('');
  const [impuestosSearchResults, setImpuestosSearchResults] = useState<any[]>([]);
  const [impuestosSearchPage, setImpuestosSearchPage] = useState(0);
  const [impuestosSearchTotal, setImpuestosSearchTotal] = useState(0);
  const [openImputableSearchImpuesto, setOpenImputableSearchImpuesto] = useState(false);
  const [imputableSearchImpuestoIndex, setImputableSearchImpuestoIndex] = useState<number | null>(null);
  const [expandOtrosImpuestos, setExpandOtrosImpuestos] = useState(false);
  const [openImputableSearchConcepto, setOpenImputableSearchConcepto] = useState(false);
  const [imputableSearchConceptoIndex, setImputableSearchConceptoIndex] = useState<number | null>(null);
  const [impuestoTasas, setImpuestoTasas] = useState<{ impuestotasa1?: number; impuestotasa2?: number; impuestotasa3?: number; impuestotasa4?: number }>({});
  const [accordionProductosExpanded, setAccordionProductosExpanded] = useState(true);
  const [accordionConceptosExpanded, setAccordionConceptosExpanded] = useState(false);
  const compraPendienteFromNav = (location.state as { compraPendiente?: CompraPendienteMaster } | null)?.compraPendiente;
  const compraPendienteNavAppliedRef = useRef(false);
  const devolverDesdeFacturaAppliedRef = useRef(false);
  const agregarPendienteProductoRef = useRef<(p: CompraPendienteMaster) => Promise<void>>(async () => {});

  const handleFormularioChange = (updates: Partial<NcFormulario>) => {
    setFormulario((f) => ({ ...f, ...updates }));
  };

  const loadCompanias = useCallback(async () => {
    try {
      const res = await api.get('/api/Compañias');
      const data = res.data;
      const list: Compania[] = Array.isArray(data) ? data : Array.isArray((data as any)?.items) ? (data as any).items : [];
      setCompanias(
        list.map((c: any) => ({
          idCompania: String(c.idCompania || '').trim(),
          nombre: String(c.nombre || '').trim(),
          condIva: c.condIva != null ? String(c.condIva).trim() : undefined
        }))
      );
    } catch {
      setCompanias([]);
    }
  }, []);

  useEffect(() => {
    loadCompanias();
  }, [loadCompanias]);

  useEffect(() => {
    variablesSistemaService.getImpuestoTasas().then(setImpuestoTasas).catch(() => ({}));
  }, []);

  useEffect(() => {
    if (companias.length > 0 && !formulario.idCompania) {
      const first = companias[0];
      if (first?.idCompania) {
        setFormulario((f) => ({ ...f, idCompania: first.idCompania }));
      }
    }
  }, [companias, formulario.idCompania]);

  const dateToYmd = (v?: string | Date | null): string | undefined => {
    if (v == null) return undefined;
    if (typeof v === 'string') return (v.split('T')[0] || v).slice(0, 10);
    return toLocalDateStringYMD(v);
  };

  const aplicarFacturaOrigenEnFormulario = async (nroFactura: number) => {
    if (!nroFactura || Number.isNaN(nroFactura) || nroFactura <= 0) {
      enqueueSnackbar('Ingrese un número de formulario de factura válido', { variant: 'warning' });
      return;
    }
    setLoadingFacturaOrigen(true);
    try {
      const f: FacturaCompra = await facturaCompraService.getFacturaCompra(nroFactura);
      const fx = f as any;
      const detalles = f.detalles ?? [];
      const hayGastos = detalles.some((d) => ((d.tipoProveedor || '').toString().trim().toUpperCase() === 'G'));
      const tipoProvSugerido = hayGastos ? 'G' : 'C';

      setFormulario((prev) => ({
        ...prev,
        nroFormularioFactura: nroFactura,
        nroProveedor: f.nroProveedor ?? prev.nroProveedor,
        letra: (() => {
          const L = ((f.letra || prev.letra || 'A') as string).trim().toUpperCase();
          return L === 'B' ? 'A' : L || 'A';
        })(),
        nroSuc: 0,
        nroComprobante: 0,
        tipoProveedor: ((f.tipoProveedor || tipoProvSugerido || prev.tipoProveedor || 'C') as string).trim().slice(0, 1).toUpperCase() || 'C',
        descripcion: f.descripcion ?? prev.descripcion,
        formaDePago: f.formaDePago || prev.formaDePago,
        nroUbicacionDestino: f.nroUbicacionDestino ?? prev.nroUbicacionDestino,
        porcDesc: Number(f.porcDesc) || prev.porcDesc,
        porcDesc2: Number(f.porcDesc2) || prev.porcDesc2,
        porcDesc3: Number(f.porcDesc3) || prev.porcDesc3,
        fechaEmision: dateToYmd(f.fechaEmision) || prev.fechaEmision,
        fechaVencim: dateToYmd(f.fechaVencim) || prev.fechaVencim,
        aplicacionIva: dateToYmd(f.aplicacionIva) || dateToYmd(f.fechaEmision) || prev.aplicacionIva,
        idCompania: String(fx.idCompania ?? prev.idCompania ?? '').trim() || prev.idCompania
      }));
      setFacturaOrigenLabel(formatoFacturaCompraCorto(f.letra, f.nroSuc, f.nroComprobante ?? undefined));
      setProveedorNombre((f.nombre || '').trim());
      setUbicacionNombre((f.ubicacionDestinoDescripcion || '').trim());

      const productos: CreateItemCompraDto[] = [];
      const conceptos: CreateItemCompraDto[] = [];
      for (const d of detalles) {
        const tipo = (d.tipoProveedor || 'C').toString().trim().toUpperCase();
        const porc = Number(d.porcDesc) || 0;
        const qty = Number(d.cantidad) || 0;
        const nroItemMaster = Number(d.nroItem) || 0;
        const masterLink =
          nroFactura > 0 && nroItemMaster > 0
            ? { nroFormularioMaster: nroFactura, nroItemMaster }
            : {};
        let costoBruto =
          Number(d.precioUnitario) > 0
            ? Number(d.precioUnitario)
            : qty > 0
              ? Math.round(((Number(d.subtotal) || 0) / qty) * 100) / 100
              : 0;
        const base = {
          codigoArticulo: (d.codigoArticulo || '').trim(),
          descripcion: (d.descripcionArticulo || '').trim(),
          cantidad: qty > 0 ? qty : 1,
          costoBrutoUnitario: costoBruto,
          porcDesc1: porc,
          porcDesc2: 0,
          porcDesc3: 0,
          porcDesc4: 0,
          porcDesc5: 0,
          notas: normalizeItemNotas(d.notas ?? null) ?? '',
          tasa1: Number(d.tasa1) || 0,
          tasa2: Number(d.tasa2) || 0,
          tasa3: Number(d.tasa3) || 0,
          tasa4: Number(d.tasa4) || 0,
          tasa5: Number(d.tasa5) || 0,
          ...masterLink,
        };
        if (tipo === 'G') {
          conceptos.push({
            ...defaultItemConcepto,
            ...base,
            tipoProveedor: 'G',
            nroCuenta: d.nroCuenta != null ? Number(d.nroCuenta) : 0
          });
        } else {
          productos.push({
            ...defaultItem,
            ...base,
            tipoProveedor: 'C'
          });
        }
      }
      setItemsProductos(productos);
      setItemsConceptos(conceptos);
      setIvaManualUsuario(null);
      setSubtotalManualUsuario(null);
      setAccordionProductosExpanded(productos.length > 0);
      setAccordionConceptosExpanded(conceptos.length > 0 && productos.length === 0);

      const impRows: ImpuestoAdicionalRow[] = (f.impuestos || [])
        .map((imp) => {
          const codigo = (imp.codigoImpuesto || '').trim();
          const cta = Number(imp.ctaCtable) || 0;
          if (!codigo || cta <= 0) return null;
          return {
            codigoImpuesto: codigo,
            ctaCtable: cta,
            descripcion: codigo,
            montoNeto: Number(imp.montoBaseImp) || 0,
            montoImpuesto: Number(imp.montoImpuesto) || 0,
            valor: 0,
            basadoen: 2,
            ctaCtableLabel: `${cta}`
          } as ImpuestoAdicionalRow;
        })
        .filter(Boolean) as ImpuestoAdicionalRow[];
      setImpuestosAdicionales(impRows);
      setExpandOtrosImpuestos(impRows.length > 0);

      setProductCache({});
      const codigos = Array.from(new Set(productos.map((it) => (it.codigoArticulo || '').trim()).filter(Boolean)));
      if (codigos.length > 0) {
        const cacheEntries = await Promise.all(
          codigos.map(async (codigo) => {
            try {
              const p = await cargarProductoParaDocumento(codigo, 'COMPRAS');
              const pr: any = p;
              return [
                codigo,
                {
                  calculaIva: typeof pr.calculaIva === 'string' ? pr.calculaIva : undefined,
                  ivaRI: pr.ivaRI != null ? Number(pr.ivaRI) : undefined,
                  ivaCF: pr.ivaCF != null ? Number(pr.ivaCF) : undefined,
                  ivaMT: pr.ivaMT != null ? Number(pr.ivaMT) : undefined,
                  ivaRN: pr.ivaRN != null ? Number(pr.ivaRN) : undefined,
                  nroTasaIva: pr.nroTasaIva != null ? Number(pr.nroTasaIva) : undefined
                }
              ] as const;
            } catch {
              return null;
            }
          })
        );
        const nextCache: Record<string, { calculaIva?: string; ivaRI?: number; ivaCF?: number; ivaMT?: number; ivaRN?: number; nroTasaIva?: number }> =
          Object.fromEntries(cacheEntries.filter(Boolean).map((e) => e as any));
        setProductCache((prev) => ({ ...prev, ...nextCache }));
      }

      enqueueSnackbar('Datos cargados desde la factura de compra', { variant: 'success' });
    } catch (e: unknown) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudo cargar la factura de compra'), { variant: 'error' });
    } finally {
      setLoadingFacturaOrigen(false);
    }
  };

  /** Desde listado de facturas: /compras/notas-credito/nuevo?devolverDesde={nroFormulario} */
  useEffect(() => {
    if (devolverDesdeFacturaAppliedRef.current) return;
    const raw = searchParams.get('devolverDesde');
    const n = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(n) || n <= 0) return;
    devolverDesdeFacturaAppliedRef.current = true;
    void aplicarFacturaOrigenEnFormulario(n);
    // Intencionalmente solo al montar / cuando cambia el query de devolución
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Incluye facturasModalQueryKey para aplicar búsqueda sin depender de cada tecla en facturasModalSearch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!openBuscarFacturaModal) return;
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) return;
    let cancelled = false;
    (async () => {
      setFacturasModalLoading(true);
      try {
        const res = await facturaCompraService.getFacturasCompra({
          pageNumber: facturasModalPage + 1,
          pageSize: facturasModalRowsPerPage,
          searchTerm: facturasModalSearch || undefined,
          fechaDesde: facturasModalFechaDesde,
          fechaHasta: facturasModalFechaHasta,
          nroProveedor: String(formulario.nroProveedor),
          soloFacturas: false
        });
        if (!cancelled) {
          setFacturasModalList(res.items || []);
          setFacturasModalTotal(res.totalCount || 0);
        }
      } catch (e) {
        if (!cancelled) {
          enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron cargar las facturas'), { variant: 'error' });
          setFacturasModalList([]);
          setFacturasModalTotal(0);
        }
      } finally {
        if (!cancelled) setFacturasModalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    openBuscarFacturaModal,
    facturasModalPage,
    facturasModalFechaDesde,
    facturasModalFechaHasta,
    facturasModalQueryKey,
    formulario.nroProveedor,
    enqueueSnackbar
  ]);

  /** Sugerir letra según TipoIva del proveedor (I/M->A, C/CF->C, X->X; resto->A) */
  const letraFromTipoIva = (tipoIva: string | undefined): string => {
    const t = (tipoIva ?? '').trim().toUpperCase();
    if (t === 'X') return 'X';
    if (t === 'C' || t === 'CF') return 'C';
    if (t === 'I' || t === 'M') return 'A';
    return 'A';
  };

  const handleProveedorClear = () => {
    setFormulario((prev) => ({ ...prev, nroProveedor: 0 }));
    setProveedorNombre('');
    setProveedorCuit('');
    setProveedorTipoIva('');
  };

  const handleProveedorSelect = async (proveedor: Proveedor) => {
    let datosProveedor: Proveedor = proveedor;
    try {
      const full = await proveedorService.getProveedor(proveedor.nroProveedor);
      datosProveedor = { ...proveedor, ...full };
    } catch {
      /* se usa la fila del buscador */
    }

    let letraSugerida = letraFromTipoIva(datosProveedor.tipoIva);

    // Si la compañía emisora está configurada como "RESP. MONOTRIBUTO",
    // la letra del comprobante siempre debe ser C.
    const companiaEmisora = companias.find((c) => c.idCompania === formulario.idCompania);
    const condIvaNorm = (companiaEmisora?.condIva ?? '')
      .toUpperCase()
      .replace(/\./g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (condIvaNorm === 'RESP MONOTRIBUTO' || condIvaNorm.includes('MONOTRIBUTO')) {
      letraSugerida = 'C';
    }

    const tipoProveedorSugerido = (datosProveedor.tipoProveedor ?? '').trim().toUpperCase() === 'G' ? 'G' : 'C';
    const fallbackFp =
      formulario.formaDePago && FORMAS_DE_PAGO.includes(formulario.formaDePago)
        ? formulario.formaDePago
        : 'CONTADO';
    const formaDePagoSugerida = formaDePagoDesdeCondVtaProveedor(FORMAS_DE_PAGO, datosProveedor, fallbackFp);
    setFormulario((f) => ({
      ...f,
      nroProveedor: datosProveedor.nroProveedor,
      letra: letraSugerida,
      tipoProveedor: tipoProveedorSugerido,
      formaDePago: formaDePagoSugerida
    }));
    setProveedorNombre(datosProveedor.nombre || '');
    setProveedorCuit(datosProveedor.cuit || '');
    setProveedorTipoIva(datosProveedor.tipoIva || '');
    if (tipoProveedorSugerido === 'G') {
      setAccordionProductosExpanded(false);
      setAccordionConceptosExpanded(true);
    } else {
      setAccordionProductosExpanded(true);
      setAccordionConceptosExpanded(false);
    }
  };

  const handleUbicacionSelect = (ubicacion: IUbicacion) => {
    setFormulario((f) => ({ ...f, nroUbicacionDestino: ubicacion.nroUbicacion }));
    setUbicacionNombre(ubicacion.nombreUbicacion || `Ubicación ${ubicacion.nroUbicacion}`);
    setOpenUbicacionSearch(false);
  };

  const applyUbicacionPredeterminada = useCallback((nro: number, nombre: string) => {
    setFormulario((f) => ({ ...f, nroUbicacionDestino: nro }));
    setUbicacionNombre(nombre);
  }, []);

  useAutoUbicacionPredeterminada({
    nroUbicacionDestino: formulario.nroUbicacionDestino,
    onApply: applyUbicacionPredeterminada,
    enabled: !loadingFacturaOrigen,
  });

  const updateItemProducto = (index: number, updates: Partial<CreateItemCompraDto>) => {
    setItemsProductos((prev) => {
      const next = [...prev];
      const shouldClearManual =
        !('subtotalManual' in updates) &&
        SUBTOTAL_RESET_KEYS.some((k) => k in updates && updates[k] !== undefined);
      next[index] = {
        ...next[index],
        ...updates,
        ...(shouldClearManual ? { subtotalManual: undefined } : {}),
      };
      return next;
    });
  };

  const addItemProducto = () => {
    setItemsProductos((prev) => [...prev, { ...defaultItem }]);
  };

  const removeItemProducto = (index: number) => {
    setItemsProductos((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItemConcepto = (index: number, updates: Partial<CreateItemCompraDto>) => {
    setItemsConceptos((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const addItemConcepto = () => {
    setItemsConceptos((prev) => [...prev, { ...defaultItemConcepto }]);
  };

  const removeItemConcepto = (index: number) => {
    setItemsConceptos((prev) => prev.filter((_, i) => i !== index));
  };

  type ProductIvaCache = { calculaIva?: string; ivaRI?: number; ivaCF?: number; ivaMT?: number; ivaRN?: number; nroTasaIva?: number };

  const getIvaPorcForLetra = (letra: string, product: ProductIvaCache | null): number => {
    if (!letra || !product) return 0;
    const L = letra.trim().toUpperCase();
    if (L === 'A') return product.ivaRI ?? 0;
    if (L === 'B') return product.ivaCF ?? 0;
    if (L === 'C') return product.ivaMT ?? 0;
    if (L === 'X') return product.ivaRN ?? 0;
    return 0;
  };

  /** Calcula tasa1-5 según stock.calculaiva, letra (A->IvaRI) y stock.NroTasaIVA.
   * Letras B, C y X no calculan IVA en compras. Factura A + nroTasaIva 4 (exento): tasa4 = monto exento con descuento aplicado. */
  const computeTasasForItem = (
    item: CreateItemCompraDto,
    product: ProductIvaCache | null,
    letra: string,
    factorBonif: number
  ): { tasa1: number; tasa2: number; tasa3: number; tasa4: number; tasa5: number } => {
    const zeros = { tasa1: 0, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: 0 };
    if (letra === 'B' || letra === 'C' || letra === 'X') return zeros;

    const subBrutoItem = subtotalEfectivoItem(item);

    const nroTasa = product?.nroTasaIva ?? 0;
    if (letra === 'A' && (nroTasa === 4 || nroTasa === 5)) {
      const montoExentoConDesc = Math.round(subBrutoItem * factorBonif * 100) / 100;
      return nroTasa === 4 ? { ...zeros, tasa4: montoExentoConDesc } : { ...zeros, tasa5: montoExentoConDesc };
    }

    if (!product || (product.calculaIva ?? '').trim().toUpperCase() !== 'S') return zeros;
    const porcentaje = getIvaPorcForLetra(letra, product);
    if (porcentaje <= 0) return zeros;
    const subNetoItem = Math.round(subBrutoItem * factorBonif * 100) / 100;
    const montoIVA = Math.round(subNetoItem * (porcentaje / 100) * 100) / 100;
    const nro = Math.min(5, Math.max(1, product.nroTasaIva ?? 1));
    switch (nro) {
      case 1: return { ...zeros, tasa1: montoIVA };
      case 2: return { ...zeros, tasa2: montoIVA };
      case 3: return { ...zeros, tasa3: montoIVA };
      case 4: return { ...zeros, tasa4: montoIVA };
      case 5: return { ...zeros, tasa5: montoIVA };
      default: return { ...zeros, tasa1: montoIVA };
    }
  };

  const subtotalItem = (item: CreateItemCompraDto) => {
    const qty = Number(item.cantidad) || 0;
    const costo = Number(item.costoBrutoUnitario) || 0;
    const d1 = 1 - (Number(item.porcDesc1) || 0) / 100;
    const d2 = 1 - (Number(item.porcDesc2) || 0) / 100;
    const d3 = 1 - (Number(item.porcDesc3) || 0) / 100;
    const d4 = 1 - (Number(item.porcDesc4) || 0) / 100;
    const d5 = 1 - (Number(item.porcDesc5) || 0) / 100;
    return Math.round(qty * costo * d1 * d2 * d3 * d4 * d5 * 100) / 100;
  };

  const subtotalEfectivoItem = (item: CreateItemCompraDto) => {
    if (item.subtotalManual !== undefined) {
      return Math.round(item.subtotalManual * 100) / 100;
    }
    return subtotalItem(item);
  };

  const letra = formulario.letra?.trim().toUpperCase() || '';

  /** Producto exento (tasa4 y tasa5 = nroTasaIva 4 y 5) en Factura A: no gravado, va a IVA Exento/Monto Exento */
  const esExento = (prod: ProductIvaCache | null) => {
    const nro = prod?.nroTasaIva ?? 0;
    return letra === 'A' && (nro === 4 || nro === 5);
  };

  const allItems = [...itemsProductos.map((it) => ({ ...it, tipoProveedor: 'C' as const })), ...itemsConceptos.map((it) => ({ ...it, tipoProveedor: 'G' as const }))];

  /** Paso 1: Subtotal Bruto = suma de TODOS los renglones (sin excluir exentos) */
  const subtotalBruto = allItems.reduce((sum, it) => sum + subtotalEfectivoItem(it), 0);

  /** Paso 2: Descuento (bonificación) aplicado sobre el Subtotal Bruto */
  const factorBonif = (1 - (Number(formulario.porcDesc) || 0) / 100) * (1 - (Number(formulario.porcDesc2) || 0) / 100) * (1 - (Number(formulario.porcDesc3) || 0) / 100);
  const totalBonificacion = Math.round(subtotalBruto * (1 - factorBonif) * 100) / 100;
  const subtotalNeto = Math.round((subtotalBruto - totalBonificacion) * 100) / 100;

  /** Nota de crédito de compra: AFIP 03 / 08 / 13 según letra */
  const computeTipoComprobanteNc = (letraValue: string): string => {
    const L = (letraValue || '').trim().toUpperCase();
    if (L === 'A') return '03';
    if (L === 'B') return '08';
    if (L === 'C') return '13';
    return '00';
  };

  useEffect(() => {
    const nuevo = computeTipoComprobanteNc(formulario.letra || '');
    if (formulario.tipoComprobante !== nuevo) {
      setFormulario((prev) => ({ ...prev, tipoComprobante: nuevo }));
    }
  }, [formulario.letra, formulario.tipoComprobante]);
  const impuestosPorTasa = new Map<number, { tasa: number; monto: number }>();
  const calculaIva = letra !== 'B' && letra !== 'C' && letra !== 'X';

  /** Paso 3: Subtotal Gravado = solo ítems gravados (tasa1, 2, 3) con descuento. Si todos son exentos (tasa4/5), es cero. */
  const subtotalGravado = calculaIva
    ? Math.round(
        allItems.reduce((sum, it) => {
          if (it.tipoProveedor === 'G') {
            const hasGravado = (it.tasa1 ?? 0) > 0 || (it.tasa2 ?? 0) > 0 || (it.tasa3 ?? 0) > 0;
            if (!hasGravado) return sum;
            const subBrutoItem = subtotalEfectivoItem(it);
            const subNetoItem = Math.round(subBrutoItem * factorBonif * 100) / 100;
            return sum + subNetoItem;
          }
          const codigo = (it.codigoArticulo || '').trim();
          const prod = codigo ? productCache[codigo] : null;
          if (esExento(prod)) return sum;
          const subBrutoItem = subtotalEfectivoItem(it);
          const subNetoItem = Math.round(subBrutoItem * factorBonif * 100) / 100;
          return sum + subNetoItem;
        }, 0) * 100
      ) / 100
    : subtotalNeto;

  allItems.forEach((it) => {
    if (!calculaIva) return;

    const subBrutoItem = subtotalEfectivoItem(it);
    const subNetoItem = Math.round(subBrutoItem * factorBonif * 100) / 100;
    const codigo = (it.codigoArticulo || '').trim();
    const prod = codigo ? productCache[codigo] : null;

    if (it.tipoProveedor === 'G') {
      const t1 = it.tasa1 ?? 0, t2 = it.tasa2 ?? 0, t3 = it.tasa3 ?? 0, t4 = it.tasa4 ?? 0, t5 = it.tasa5 ?? 0;
      if (t1 > 0) {
        const tasa = impuestoTasas.impuestotasa1 ?? 21;
        if (impuestosPorTasa.has(tasa)) impuestosPorTasa.get(tasa)!.monto += t1;
        else impuestosPorTasa.set(tasa, { tasa, monto: t1 });
      }
      if (t2 > 0) {
        const tasa = impuestoTasas.impuestotasa2 ?? 10.5;
        if (impuestosPorTasa.has(tasa)) impuestosPorTasa.get(tasa)!.monto += t2;
        else impuestosPorTasa.set(tasa, { tasa, monto: t2 });
      }
      if (t3 > 0) {
        const tasa = impuestoTasas.impuestotasa3 ?? 27;
        if (impuestosPorTasa.has(tasa)) impuestosPorTasa.get(tasa)!.monto += t3;
        else impuestosPorTasa.set(tasa, { tasa, monto: t3 });
      }
      if (t4 > 0 || t5 > 0) {
        const montoExento = t4 + t5;
        if (impuestosPorTasa.has(0)) impuestosPorTasa.get(0)!.monto += montoExento;
        else impuestosPorTasa.set(0, { tasa: 0, monto: montoExento });
      }
      return;
    }

    if (esExento(prod)) {
      /** Paso 4: Monto exento = ítems exentos menos descuentos (se aplica factorBonif) */
      const montoExento = Math.round(subBrutoItem * factorBonif * 100) / 100;
      if (impuestosPorTasa.has(0)) {
        impuestosPorTasa.get(0)!.monto += montoExento;
      } else {
        impuestosPorTasa.set(0, { tasa: 0, monto: montoExento });
      }
      return;
    }

    const ivaPorc = getIvaPorcForLetra(letra, prod);
    const montoIVAItem = Math.round(subNetoItem * (ivaPorc / 100) * 100) / 100;
    if (ivaPorc > 0 && montoIVAItem > 0) {
      const tasa = ivaPorc;
      if (impuestosPorTasa.has(tasa)) {
        const exist = impuestosPorTasa.get(tasa)!;
        exist.monto += montoIVAItem;
      } else {
        impuestosPorTasa.set(tasa, { tasa, monto: montoIVAItem });
      }
    }
  });
  const impuestosArray = Array.from(impuestosPorTasa.values()).sort((a, b) => a.tasa - b.tasa);
  const montoIva = Math.round(impuestosArray.reduce((s, i) => s + i.monto, 0) * 100) / 100;
  const subtotalResumenCalculado = letra === 'A' ? subtotalGravado : subtotalNeto;
  const subtotalResumenEfectivo =
    subtotalManualUsuario !== null
      ? Math.round(subtotalManualUsuario * 100) / 100
      : subtotalResumenCalculado;
  /** IVA mostrado: manual, o proporcional si solo se editó el subtotal, o calculado. */
  const ivaEfectivo =
    ivaManualUsuario !== null
      ? Math.round(ivaManualUsuario * 100) / 100
      : subtotalManualUsuario !== null && subtotalResumenCalculado > 0
        ? Math.round(montoIva * (subtotalResumenEfectivo / subtotalResumenCalculado) * 100) / 100
        : montoIva;
  const totalImpuestosAdicionales = Math.round(impuestosAdicionales.reduce((s, i) => s + i.montoImpuesto, 0) * 100) / 100;
  // En letra C no calculamos impuestos en `impuestosPorTasa`, pero igualmente el usuario puede cargar
  // Exento/No Grav. (tasa4/tasa5). El backend guarda `Exento = Tasa4 + Tasa5`.
  const montoExentoLineas = Math.round(
    allItems.reduce((sum, it) => sum + (it.tasa4 ?? 0) + (it.tasa5 ?? 0), 0) * 100
  ) / 100;

  const totalFinal = Math.round(
    (subtotalResumenEfectivo + ivaEfectivo + totalImpuestosAdicionales + (letra === 'C' ? montoExentoLineas : 0)) * 100
  ) / 100;

  const calcMontoImpuesto = (row: ImpuestoAdicionalRow, montoNeto: number): number => {
    const basadoen = row.basadoen ?? 1;
    const valor = row.valor ?? 0;
    let calc = 0;
    if (basadoen === 1 && valor !== 0) {
      calc = montoNeto * (valor / 100);
    } else if (basadoen === 2) {
      calc = valor;
    }
    if (row.montoGravMin != null && montoNeto < row.montoGravMin) return 0;
    if (row.montoGravMax != null && montoNeto > row.montoGravMax) calc = (row.montoGravMax ?? 0) * (valor / 100);
    if (row.montoImpuestoMin != null && calc < row.montoImpuestoMin) calc = row.montoImpuestoMin;
    if (row.montoImpuestoMax != null && calc > row.montoImpuestoMax) calc = row.montoImpuestoMax;
    return Math.round(calc * 100) / 100;
  };

  const loadProductos = useCallback(async () => {
    if (!openProductSearch) return;
    try {
      const paramsBloqueo = await paramsBusquedaProductoDocumento('COMPRAS');
      setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
      const res = await productoService.getProductos({
        searchTerm: productSearchTerm || undefined,
        pageNumber: productosPage + 1,
        pageSize: productosRowsPerPage,
        ...paramsBloqueo,
      });
      setProductos(res.items || []);
      setProductosTotalCount(res.totalCount || 0);
    } catch {
      setProductos([]);
      setProductosTotalCount(0);
    }
  }, [openProductSearch, productSearchTerm, productosPage, productosRowsPerPage]);

  useEffect(() => {
    if (openProductSearch) {
      loadProductos();
    }
  }, [openProductSearch, loadProductos]);

  const loadImpuestosSearch = useCallback(async () => {
    if (!openImpuestoSearch) return;
    try {
      const res = await impuestoService.getImpuestos({
        searchTerm: impuestoSearchTerm || undefined,
        pageNumber: impuestosSearchPage + 1,
        pageSize: 10
      });
      const list = res.items || [];
      setImpuestosSearchResults(list);
      setImpuestosSearchTotal(res.totalCount || 0);
    } catch {
      setImpuestosSearchResults([]);
      setImpuestosSearchTotal(0);
    }
  }, [openImpuestoSearch, impuestoSearchTerm, impuestosSearchPage]);

  useEffect(() => {
    if (openImpuestoSearch) loadImpuestosSearch();
  }, [openImpuestoSearch, loadImpuestosSearch]);

  useEffect(() => {
    if (compraPendienteNavAppliedRef.current) return;
    if (!compraPendienteFromNav) return;

    compraPendienteNavAppliedRef.current = true;
    const p = compraPendienteFromNav;
    const np = p.nroProveedor;
    if (np && np > 0) {
      setFormulario((prev) => ({ ...prev, nroProveedor: np }));
      setProveedorNombre(p.nombreProveedor || '');
    }
    void (async () => {
      await agregarPendienteProductoRef.current(p);
      setAccordionProductosExpanded(true);
      setAccordionConceptosExpanded(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compraPendienteFromNav]);

  const handleSelectImpuesto = async (imp: any) => {
    const codigo = (imp.codigoImpuesto || imp.CodigoImpuesto || '').trim();
    if (!codigo) return;
    try {
      const detalle = await impuestoService.getImpuesto(codigo);
      const d = detalle as any;
      const cta = d.ctaCtable ?? d.CtaCtable;
      if (cta == null || cta === 0) {
        enqueueSnackbar('Este impuesto no tiene cuenta contable configurada', { variant: 'warning' });
        return;
      }
      const valor = d.valor ?? d.Valor ?? 0;
      const basadoen = d.basadoen ?? d.Basadoen ?? 1;
      const montoNeto = 0;
      const montoImpuesto = basadoen === 1 ? 0 : (typeof valor === 'number' ? valor : Number(valor) || 0);
      const newRow: ImpuestoAdicionalRow = {
        codigoImpuesto: codigo,
        ctaCtable: Number(cta),
        descripcion: (d.descripcion || d.Descripcion || codigo).trim(),
        montoNeto,
        montoImpuesto: Math.round(montoImpuesto * 100) / 100,
        valor: typeof valor === 'number' ? valor : Number(valor) || 0,
        basadoen,
        montoGravMin: d.montoGravMin ?? d.MontoGravMin,
        montoGravMax: d.montoGravMax ?? d.MontoGravMax,
        montoImpuestoMin: d.montoImpuestoMin ?? d.MontoImpuestoMin,
        montoImpuestoMax: d.montoImpuestoMax ?? d.MontoImpuestoMax,
        ctaCtableLabel: `${cta}`
      };
      setImpuestosAdicionales((prev) => [...prev, newRow]);
      setExpandOtrosImpuestos(true);
      setOpenImpuestoSearch(false);
      setImpuestoSearchTerm('');
      setImpuestosSearchPage(0);
    } catch {
      enqueueSnackbar('Error al cargar el impuesto', { variant: 'error' });
    }
  };

  const updateImpuestoAdicional = (idx: number, updates: Partial<ImpuestoAdicionalRow>) => {
    setImpuestosAdicionales((prev) => {
      const next = [...prev];
      const row = next[idx];
      if (!row) return prev;
      const merged = { ...row, ...updates };
      if ('montoNeto' in updates && updates.montoNeto !== undefined) {
        merged.montoImpuesto = calcMontoImpuesto(merged, merged.montoNeto);
      }
      next[idx] = merged;
      return next;
    });
  };

  const removeImpuestoAdicional = (idx: number) => {
    setImpuestosAdicionales((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleImputableSelectImpuesto = (imputable: Imputable) => {
    const idx = imputableSearchImpuestoIndex;
    if (idx !== null) {
      updateImpuestoAdicional(idx, {
        ctaCtable: imputable.nroCuenta,
        ctaCtableLabel: imputable.nomCuenta || imputable.descripcioncta || `${imputable.nroCuenta}`
      });
    }
    setOpenImputableSearchImpuesto(false);
    setImputableSearchImpuestoIndex(null);
  };

  const handleSelectImputableConcepto = (imputable: Imputable) => {
    const idx = imputableSearchConceptoIndex;
    if (idx !== null) {
      updateItemConcepto(idx, {
        codigoArticulo: String(imputable.nroCuenta),
        descripcion: imputable.nomCuenta || imputable.descripcioncta || '',
        nroCuenta: imputable.nroCuenta,
        tipoProveedor: 'G'
      });
    }
    setOpenImputableSearchConcepto(false);
    setImputableSearchConceptoIndex(null);
  };

  const handleSelectProduct = async (p: any) => {
    const idx = productSearchIndex;
    if (idx !== null) {
      const codigo = p.codigoArticulo || p.codigo || '';
      clearRechazoDiscontinuado('COMPRAS', codigo);
      if (esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis)) {
        enqueueSnackbar(mensajeArticuloDiscontinuadoBloqueado(codigo), { variant: 'warning' });
        return;
      }
      const decision = await evaluarUsoProductoDocumento(
        {
          codigoArticulo: codigo,
          tipoArticulo: p.tipoArticulo,
          descripcion: p.descripcion || p.descripcionArticulo,
        },
        'COMPRAS',
      );
      if (!decision.permitido) {
        if (decision.motivo !== 'discontinuado_rechazado') {
          enqueueSnackbar(decision.mensaje, { variant: 'warning' });
        }
        return;
      }
      const desc = p.descripcion || p.descripcionArticulo || '';
      const costo = Number(p.costo) || 0;
      updateItemProducto(idx, { codigoArticulo: codigo, descripcion: desc, costoBrutoUnitario: costo });
      setProductCache((prev) => ({
        ...prev,
        [codigo]: {
          calculaIva: typeof p.calculaIva === 'string' ? p.calculaIva : undefined,
          ivaRI: p.ivaRI != null ? Number(p.ivaRI) : undefined,
          ivaCF: p.ivaCF != null ? Number(p.ivaCF) : undefined,
          ivaMT: p.ivaMT != null ? Number(p.ivaMT) : undefined,
          ivaRN: p.ivaRN != null ? Number(p.ivaRN) : undefined,
          nroTasaIva: p.nroTasaIva != null ? Number(p.nroTasaIva) : undefined
        }
      }));
    }
    setOpenProductSearch(false);
    setProductSearchIndex(null);
  };

  const handleCodigoBlurOrEnter = useCallback(async (idx: number, codigoValue: string) => {
    const codigo = (codigoValue || '').trim();
    if (!codigo) return;
    try {
      const modo = (await paramsBusquedaProductoDocumento('COMPRAS')).modoBloqueoArtDiscontinuados ?? 1;
      setModoBloqueoDis(modo);
      const p = await cargarProductoParaDocumento(codigo, 'COMPRAS');
      if (esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modo)) {
        enqueueSnackbar(mensajeArticuloDiscontinuadoBloqueado(codigo), { variant: 'warning' });
        return;
      }
      const decision = await evaluarUsoProductoDocumento(
        {
          codigoArticulo: codigo,
          tipoArticulo: p.tipoArticulo,
          descripcion: p.descripcion || (p as any).descripcionArticulo,
        },
        'COMPRAS',
      );
      if (!decision.permitido) {
        if (decision.motivo === 'discontinuado_rechazado') {
          updateItemProducto(idx, { codigoArticulo: '', descripcion: '', costoBrutoUnitario: 0 });
        } else {
          enqueueSnackbar(decision.mensaje, { variant: 'warning' });
        }
        return;
      }
      const desc = p.descripcion || (p as any).descripcionArticulo || '';
      const costo = Number(p.costo) || Number((p as any).costoBrutoUnitario) || 0;
      updateItemProducto(idx, { codigoArticulo: codigo, descripcion: desc, costoBrutoUnitario: costo });
      const pr = p as any;
      setProductCache((prev) => ({
        ...prev,
        [codigo]: {
          calculaIva: typeof pr.calculaIva === 'string' ? pr.calculaIva : undefined,
          ivaRI: pr.ivaRI != null ? Number(pr.ivaRI) : undefined,
          ivaCF: pr.ivaCF != null ? Number(pr.ivaCF) : undefined,
          ivaMT: pr.ivaMT != null ? Number(pr.ivaMT) : undefined,
          ivaRN: pr.ivaRN != null ? Number(pr.ivaRN) : undefined,
          nroTasaIva: pr.nroTasaIva != null ? Number(pr.nroTasaIva) : undefined
        }
      }));
    } catch {
      enqueueSnackbar(`No se encontró el producto con código "${codigo}"`, { variant: 'warning' });
    }
  }, [enqueueSnackbar]);

  const openBuscarLineasFacturadas = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Primero seleccione un proveedor', { variant: 'warning' });
      return;
    }
    setOpenPendientes(true);
    setLoadingPendientes(true);
    try {
      const data = await comprasSeguimientoService.getPendientesMaster(formulario.nroProveedor, false, true);
      const rows = (data || []).filter((p) => cantidadFacturadaNetaDisponible(p) > 0);
      setPendientes(rows);
      if (rows.length === 0) {
        enqueueSnackbar('No hay líneas con cantidad ya facturada para este proveedor', { variant: 'info' });
      }
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron cargar las líneas facturadas'), { variant: 'error' });
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const abrirModalDevolverDesdeFactura = () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Primero seleccione un proveedor', { variant: 'warning' });
      return;
    }
    setFacturasModalFechaDesde(getFirstDayOfMonthYmd());
    setFacturasModalFechaHasta(getLastDayOfMonthYmd());
    setFacturasModalSearch('');
    setFacturasModalPage(0);
    setFacturasModalQueryKey((k) => k + 1);
    setOpenBuscarFacturaModal(true);
  };

  const seleccionarFacturaDesdeModal = async (nroFormulario: number) => {
    setOpenBuscarFacturaModal(false);
    await aplicarFacturaOrigenEnFormulario(nroFormulario);
  };

  /** Quita vínculo de cabecera y masters de líneas que apuntaban a esa factura. */
  const quitarVinculoFacturaOrigen = () => {
    const nroFac = formulario.nroFormularioFactura;
    setFormulario((prev) => ({ ...prev, nroFormularioFactura: undefined }));
    setFacturaOrigenLabel('');
    if (nroFac != null && nroFac > 0) {
      const stripMaster = (it: CreateItemCompraDto): CreateItemCompraDto => {
        if (it.nroFormularioMaster !== nroFac) return it;
        return {
          ...it,
          nroFormularioMaster: undefined,
          nroItemMaster: undefined,
        };
      };
      setItemsProductos((prev) => prev.map(stripMaster));
      setItemsConceptos((prev) => prev.map(stripMaster));
    }
    enqueueSnackbar('Se quitó el vínculo con la factura origen', { variant: 'info' });
  };

  const agregarPendienteProducto = async (p: CompraPendienteMaster, opts?: { cantidadDesdeFacturado?: boolean }) => {
    const esG = (p.tipoProveedorMaster || '').trim().toUpperCase() === 'G';
    const cantidad = opts?.cantidadDesdeFacturado ? cantidadSugeridaDesdeFacturadoCompra(p) : cantidadSugeridaDesdePendiente(p);
    let costoBruto = costoBrutoDesdePendienteMaster(p);
    const { porcDesc1, porcDesc2, porcDesc3, porcDesc4, porcDesc5 } = porcDescDesdePendienteMaster(p);
    if (!esG && costoBruto <= 0 && (p.codigoArticulo || '').trim()) {
      try {
        const pr = await cargarProductoParaDocumento((p.codigoArticulo || '').trim(), 'COMPRAS');
        costoBruto = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
      } catch {
        /* sin línea master ni artículo */
      }
    }
    if (esG) {
      const nroCuenta = parseInt(String(p.codigoArticulo || '').trim(), 10) || 0;
      setItemsConceptos((prev) => {
        const exists = prev.some(
          (it) =>
            it.nroFormularioMaster === p.nroFormularioMaster &&
            it.nroItemMaster === p.nroItemMaster
        );
        if (exists) return prev;
        return [
          ...prev,
          {
            ...defaultItemConcepto,
            codigoArticulo: (p.codigoArticulo || '').trim(),
            descripcion: (p.descripcionArticulo || '').trim(),
            cantidad,
            costoBrutoUnitario: costoBruto,
            porcDesc1,
            porcDesc2,
            porcDesc3,
            porcDesc4,
            porcDesc5,
            nroCuenta,
            nroFormularioMaster: p.nroFormularioMaster,
            nroItemMaster: p.nroItemMaster
          }
        ];
      });
      setAccordionConceptosExpanded(true);
      return;
    }
    setItemsProductos((prev) => {
      const exists = prev.some(
        (it) =>
          it.nroFormularioMaster === p.nroFormularioMaster &&
          it.nroItemMaster === p.nroItemMaster
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          ...defaultItem,
          codigoArticulo: p.codigoArticulo || '',
          descripcion: p.descripcionArticulo || '',
          cantidad,
          costoBrutoUnitario: costoBruto,
          porcDesc1,
          porcDesc2,
          porcDesc3,
          porcDesc4,
          porcDesc5,
          nroFormularioMaster: p.nroFormularioMaster,
          nroItemMaster: p.nroItemMaster
        }
      ];
    });
  };
  agregarPendienteProductoRef.current = agregarPendienteProducto;

  const handleSubmit = async () => {
    if (!formulario.idCompania?.trim()) {
      enqueueSnackbar('Debe seleccionar una compañía', { variant: 'warning' });
      return;
    }
    if (!formulario.letra?.trim()) {
      enqueueSnackbar('La letra es requerida', { variant: 'warning' });
      return;
    }
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Debe seleccionar un proveedor', { variant: 'warning' });
      return;
    }
    if (!formulario.nroSuc || formulario.nroSuc <= 0) {
      enqueueSnackbar('Debe ingresar una sucursal válida', { variant: 'warning' });
      return;
    }
    if (
      !loadingSucursales &&
      sucursales.length > 0 &&
      !sucursales.some((s) => Number(s.nroSuc) === Number(formulario.nroSuc))
    ) {
      enqueueSnackbar('La sucursal no está asignada a su usuario', { variant: 'warning' });
      return;
    }
    if (!formulario.formaDePago?.trim()) {
      enqueueSnackbar('La forma de pago es requerida', { variant: 'warning' });
      return;
    }
    if (!formulario.nroUbicacionDestino || formulario.nroUbicacionDestino <= 0) {
      enqueueSnackbar('Debe seleccionar una ubicación destino', { variant: 'warning' });
      return;
    }
    if (!formulario.tipoComprobante?.trim()) {
      enqueueSnackbar('El tipo de comprobante es requerido', { variant: 'warning' });
      return;
    }

    const letraValue = formulario.letra?.trim().toUpperCase() || '';
    const itemsProductosValidos = itemsProductos.filter(
      (it) => (it.codigoArticulo || '').trim() && Number(it.cantidad) > 0 && Number(it.costoBrutoUnitario) > 0
    );
    // En letra C es común cargar "Exento/No Grav." (tasa4/tasa5) pero tener "Neto Grav/Unit" (costoBrutoUnitario) en 0.
    // El backend igual exige CostoBrutoUnitario > 0, así que aceptamos el ítem si tiene exento y luego derivamos el costo al armar el payload.
    const itemsConceptosValidos = itemsConceptos.filter((it) => {
      const hasCodigo = (it.codigoArticulo || '').trim().length > 0;
      const hasNroCuenta = (it.nroCuenta ?? 0) > 0;
      const qty = Number(it.cantidad) || 0;
      const costoUnit = Number(it.costoBrutoUnitario) || 0;
      const exentoTotal = (Number(it.tasa4) || 0) + (Number(it.tasa5) || 0);
      if (!hasCodigo || !hasNroCuenta || qty <= 0) return false;
      // Si está completo por costo unitario, ok.
      if (costoUnit > 0) return true;
      // Si es letra C y se cargó exento, aceptamos para luego derivar costo.
      return letraValue === 'C' && exentoTotal > 0;
    });
    const itemsValidos = [...itemsProductosValidos.map((it) => ({ ...it, tipoProveedor: 'C' as const })), ...itemsConceptosValidos.map((it) => ({ ...it, tipoProveedor: 'G' as const }))];

    // TEMPORAL: deshabilitar control de frontend.
    // Nota: el backend todavía valida que cada item tenga CostoBrutoUnitario > 0 y Cantidad > 0.
    // Este bloqueo evita que el usuario pueda enviar en casos donde la UI está completando
    // otros campos (p.ej. Exento/No Grav.) que no impactan en `costoBrutoUnitario`.
    // if (itemsValidos.length === 0) {
    //   enqueueSnackbar('Debe agregar al menos un ítem válido (producto o concepto) con código, cantidad y costo/neto mayor a cero', { variant: 'warning' });
    //   return;
    // }

    const factorBonif = (1 - (Number(formulario.porcDesc) || 0) / 100) * (1 - (Number(formulario.porcDesc2) || 0) / 100) * (1 - (Number(formulario.porcDesc3) || 0) / 100);
    const letraVal = formulario.letra?.trim().toUpperCase() || '';
    const tipoProveedorForm = itemsProductosValidos.length > 0 ? 'C' : 'G';

    const impuestosPayload = impuestosAdicionales
      .filter((ia) => ia.codigoImpuesto?.trim() && ia.ctaCtable != null && ia.ctaCtable > 0)
      .map((ia) => ({
        codigoImpuesto: ia.codigoImpuesto.trim(),
        ctaCtable: ia.ctaCtable,
        montoBaseImp: ia.montoNeto || undefined,
        montoImpuesto: ia.montoImpuesto || undefined
      }));

    const ivaManualRounded =
      ivaManualUsuario !== null ? Math.round(ivaManualUsuario * 100) / 100 : undefined;
    const subtotalManualRounded =
      subtotalManualUsuario !== null ? Math.round(subtotalManualUsuario * 100) / 100 : undefined;

    /** Materializar totales editados en ítems: el API de NC recalcula desde qty × costo (no usa subtotalManual). */
    let itemsParaEnviar: CreateItemCompraDto[] = itemsValidos.map((it) => {
      if (it.subtotalManual === undefined) return { ...it };
      const qty = Number(it.cantidad) || 1;
      const sub = round2Nc(it.subtotalManual);
      return {
        ...it,
        costoBrutoUnitario: qty > 0 ? round2Nc(sub / qty) : sub,
        porcDesc1: 0,
        porcDesc2: 0,
        porcDesc3: 0,
        porcDesc4: 0,
        porcDesc5: 0,
        subtotalManual: undefined,
      };
    });
    if (subtotalManualRounded !== undefined) {
      itemsParaEnviar = aplicarSubtotalManualEnItems(
        itemsParaEnviar,
        subtotalManualRounded,
        factorBonif,
        subtotalEfectivoItem,
      );
    }
    // Solo forzar tasas si el usuario editó el IVA; si solo tocó subtotal, computeTasasForItem recalcula.
    if (ivaManualRounded !== undefined) {
      itemsParaEnviar = aplicarIvaManualEnItems(itemsParaEnviar, ivaManualRounded);
    }

    const nroFac = Number(formulario.nroFormularioFactura) || 0;
    const payload: NotaCreditoCompraCreatePayload = {
      formulario: {
        ...formulario,
        tipoProveedor: formulario.tipoProveedor || tipoProveedorForm,
        fechaEmision: formulario.fechaEmision || toLocalDateStringYMD(),
        fechaVencim: formulario.fechaVencim || undefined,
        aplicacionIva: formulario.aplicacionIva || formulario.fechaEmision || toLocalDateStringYMD(),
        // Criterio: si hay factura seleccionada, siempre enviar vínculo de cabecera (aunque haya ítems manuales).
        nroFormularioFactura: nroFac > 0 ? nroFac : undefined,
        ...(ivaManualRounded !== undefined ? { ivaManual: ivaManualRounded } : {}),
      },
      items: itemsParaEnviar.map((it) => {
        const codigo = (it.codigoArticulo || '').trim();
        const esConcepto = it.tipoProveedor === 'G';
        const masterForm = Number(it.nroFormularioMaster) || 0;
        const masterItem = Number(it.nroItemMaster) || 0;
        const masterFields =
          masterForm > 0 && masterItem > 0
            ? { nroFormularioMaster: masterForm, nroItemMaster: masterItem }
            : {};
        if (esConcepto) {
          const qty = Number(it.cantidad) || 0;
          const exentoTotal = (Number(it.tasa4) || 0) + (Number(it.tasa5) || 0);
          const costoUnitRaw = Number(it.costoBrutoUnitario) || 0;
          // Backend exige CostoBrutoUnitario > 0. Si el usuario cargó el exento pero no el neto unit, lo derivamos desde (tasa4+tasa5)/cantidad.
          const costoUnitDerived =
            costoUnitRaw > 0
              ? costoUnitRaw
              : letraValue === 'C' && exentoTotal > 0 && qty > 0
                ? Math.round((exentoTotal / qty) * 100) / 100
                : 0;
          return {
            codigoArticulo: codigo,
            descripcion: it.descripcion?.trim(),
            cantidad: Number(it.cantidad) || 0,
            costoBrutoUnitario: costoUnitDerived,
            porcDesc1: 0,
            porcDesc2: 0,
            porcDesc3: 0,
            porcDesc4: 0,
            porcDesc5: 0,
            um: it.um?.trim() || 'UN',
            codigoMoneda: it.codigoMoneda,
            cotizacion: it.cotizacion,
            notas: normalizeItemNotas(it.notas),
            tasa1: Number(it.tasa1) || 0,
            tasa2: Number(it.tasa2) || 0,
            tasa3: Number(it.tasa3) || 0,
            tasa4: Number(it.tasa4) || 0,
            tasa5: Number(it.tasa5) || 0,
            impInterno1: 0,
            impInterno2: 0,
            tasaHidrica: 0,
            tipoProveedor: 'G' as const,
            nroCuenta: it.nroCuenta,
            ...masterFields,
          };
        }
        const prod = codigo ? productCache[codigo] : null;
        // Con IVA manual no recalcular tasas desde el producto: usar las ya aplicadas.
        const tasas =
          ivaManualRounded !== undefined
            ? {
                tasa1: Number(it.tasa1) || 0,
                tasa2: Number(it.tasa2) || 0,
                tasa3: Number(it.tasa3) || 0,
                tasa4: Number(it.tasa4) || 0,
                tasa5: Number(it.tasa5) || 0,
              }
            : computeTasasForItem(it, prod, letraVal, factorBonif);
        return {
          codigoArticulo: codigo,
          descripcion: it.descripcion?.trim(),
          cantidad: Number(it.cantidad) || 0,
          costoBrutoUnitario: Number(it.costoBrutoUnitario) || 0,
          porcDesc1: Number(it.porcDesc1) || 0,
          porcDesc2: Number(it.porcDesc2) || 0,
          porcDesc3: Number(it.porcDesc3) || 0,
          porcDesc4: Number(it.porcDesc4) || 0,
          porcDesc5: Number(it.porcDesc5) || 0,
          um: it.um?.trim(),
          codigoMoneda: it.codigoMoneda,
          cotizacion: it.cotizacion,
          notas: normalizeItemNotas(it.notas),
          tasa1: tasas.tasa1,
          tasa2: tasas.tasa2,
          tasa3: tasas.tasa3,
          tasa4: tasas.tasa4,
          tasa5: tasas.tasa5,
          impInterno1: 0,
          impInterno2: 0,
          tasaHidrica: 0,
          tipoProveedor: 'C' as const,
          ...masterFields,
        };
      }),
      impuestos: impuestosPayload.length > 0 ? impuestosPayload : undefined
    };

    try {
      setSaving(true);
      const created = await notaCreditoCompraService.createNotaCreditoCompra(payload);
      enqueueSnackbar('Nota de crédito de compra creada correctamente', { variant: 'success' });
      navigate(`/compras/notas-credito/${created.nroFormulario}`);
    } catch (error: unknown) {
      const msg = getApiErrorMessage(error, 'Error al crear la nota de crédito de compra');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const fechaEmisionStr = formulario.fechaEmision || toLocalDateStringYMD();
  const fechaVencimStr = formulario.fechaVencim || '';
  const fechaAplicacionIvaStr = formulario.aplicacionIva || formulario.fechaEmision || toLocalDateStringYMD();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent
          sx={{
            p: { xs: 2, sm: 3 },
            // (sin overrides de tipografía para mantener el tamaño por defecto)
          }}
          onKeyDown={handleEnterAsTabNavigation}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }} flexWrap="wrap">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/compras/notas-credito')}
              sx={{ textTransform: 'none' }}
            >
              Notas de Crédito
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Nueva Nota de Crédito de Compra
            </Typography>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <SearchableSelect
                label="Compañía"
                required
                value={formulario.idCompania || null}
                onChange={(v) => handleFormularioChange({ idCompania: v ?? '' })}
                options={companias.map((c) => ({
                  value: c.idCompania,
                  label: `${c.idCompania} - ${c.nombre}`,
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProveedorSelectorField
                label="Proveedor"
                required
                value={
                  formulario.nroProveedor > 0 && proveedorNombre
                    ? {
                        nroProveedor: formulario.nroProveedor,
                        nombre: proveedorNombre,
                        cuit: proveedorCuit || undefined,
                        tipoIva: proveedorTipoIva || undefined,
                      }
                    : null
                }
                onSelect={handleProveedorSelect}
                onClear={handleProveedorClear}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <SearchableSelect
                label="Letra"
                required
                value={formulario.letra || 'A'}
                onChange={(v) => handleFormularioChange({ letra: v ?? 'A' })}
                options={[
                  { value: 'A', label: 'A' },
                  { value: 'C', label: 'C' },
                  { value: 'X', label: 'X' },
                ]}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Sucursal"
                type="text"
                value={String(formulario.nroSuc ?? 0)}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                  if (v === '') {
                    onSucursalChange(0);
                    return;
                  }
                  const n = parseInt(v, 10);
                  if (Number.isNaN(n)) return;
                  const next = n === 0 ? 0 : Math.min(99999, Math.max(1, n));
                  onSucursalChange(next);
                }}
                inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric', maxLength: 5 }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Nro. Comprobante"
                type="text"
                value={String(formulario.nroComprobante ?? 0)}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                  if (v === '') {
                    handleFormularioChange({ nroComprobante: 0 });
                    return;
                  }
                  const n = parseInt(v, 10);
                  if (Number.isNaN(n)) return;
                  const next = n === 0 ? 0 : Math.min(99999999, Math.max(1, n));
                  handleFormularioChange({ nroComprobante: next });
                }}
                inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric', maxLength: 8 }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <SearchableSelect
                label="Tipo Prov."
                required
                value={formulario.tipoProveedor || 'C'}
                onChange={(v) => handleFormularioChange({ tipoProveedor: v ?? 'C' })}
                options={[
                  { value: 'C', label: 'Compras (C)' },
                  { value: 'G', label: 'Gastos (G)' },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Tipo Comprob."
                value={formulario.tipoComprobante || ''}
                InputProps={{ readOnly: true }}
                placeholder="Se calcula según letra (NC compra)"
                required
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Fecha emisión"
                type="date"
                value={fechaEmisionStr}
                onChange={(e) => handleFormularioChange({ fechaEmision: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Fecha vencimiento"
                type="date"
                value={fechaVencimStr}
                onChange={(e) => handleFormularioChange({ fechaVencim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Fecha de Aplicación IVA"
                type="date"
                value={fechaAplicacionIvaStr}
                onChange={(e) => handleFormularioChange({ aplicacionIva: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                label="Forma de pago"
                required
                value={formulario.formaDePago || 'CONTADO'}
                onChange={(v) => handleFormularioChange({ formaDePago: v ?? 'CONTADO' })}
                options={FORMAS_DE_PAGO.map((fp) => ({ value: fp, label: fp }))}
              />
            </Grid>            
            <Grid item xs={12}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: '100%' }}
              >
                <TextField
                  fullWidth
                  label="Ubicación destino"
                  value={ubicacionNombre || ''}
                  sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setOpenUbicacionSearch(true)} edge="end" size="small">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  onClick={() => setOpenUbicacionSearch(true)}
                  required
                />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                  <Button
                    variant="outlined"
                    onClick={abrirModalDevolverDesdeFactura}
                    disabled={loadingFacturaOrigen || !formulario.nroProveedor || formulario.nroProveedor <= 0}
                    sx={{ color: '#1976d2', borderColor: '#1976d2', whiteSpace: 'nowrap' }}
                  >
                    Devolver desde factura
                  </Button>
                  {loadingFacturaOrigen ? <CircularProgress size={22} sx={{ color: '#1976d2' }} /> : null}
                </Stack>
              </Stack>
              {formulario.nroFormularioFactura != null && formulario.nroFormularioFactura > 0 ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`Factura origen: ${facturaOrigenLabel || `#${formulario.nroFormularioFactura}`} (Nº int. ${formulario.nroFormularioFactura})`}
                  />
                  <Button size="small" onClick={quitarVinculoFacturaOrigen} sx={{ textTransform: 'none' }}>
                    Quitar vínculo
                  </Button>
                </Stack>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <ObservacionesConNotaProveedor nroProveedor={formulario.nroProveedor}>
                <TextField
                  fullWidth
                  label="Observaciones"
                  value={formulario.descripcion || ''}
                  onChange={(e) => handleFormularioChange({ descripcion: e.target.value })}
                />
              </ObservacionesConNotaProveedor>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, mt: 3, color: '#1976d2' }}>
            Detalle
          </Typography>
          {!formulario.nroProveedor || formulario.nroProveedor <= 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seleccione un proveedor para poder cargar ítems.
            </Typography>
          ) : null}
          <Box sx={{ mb: 2, opacity: !formulario.nroProveedor || formulario.nroProveedor <= 0 ? 0.5 : 1, pointerEvents: !formulario.nroProveedor || formulario.nroProveedor <= 0 ? 'none' : 'auto' }}>
            <Accordion expanded={accordionProductosExpanded} onChange={(_, exp) => setAccordionProductosExpanded(!!exp)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0, marginLeft: 0.5 },
                  '& .MuiAccordionSummary-content': { margin: 0, marginRight: 0 }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#424242' }}>Items de Compras</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Cant.</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Costo unit.</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">% Desc 1</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Subtotal</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemsProductos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                            Sin ítems de producto. Use el botón Agregar producto para añadir.
                          </TableCell>
                        </TableRow>
                      ) : (
                      itemsProductos.map((item, idx) => (
                        <TableRow key={`p-${idx}`}>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.codigoArticulo || ''}
                              onChange={(e) => {
                                clearRechazoDiscontinuado('COMPRAS', item.codigoArticulo);
                                clearRechazoDiscontinuado('COMPRAS', e.target.value);
                                updateItemProducto(idx, { codigoArticulo: e.target.value });
                              }}
                              onBlur={(e) => handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                                }
                              }}
                              placeholder="Código"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setProductSearchIndex(idx);
                                        setOpenProductSearch(true);
                                      }}
                                    >
                                      <SearchIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }}
                              sx={{ minWidth: 120 }}
                            />
                          </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.descripcion || ''}
                                onChange={(e) => updateItemProducto(idx, { descripcion: e.target.value })}
                                placeholder="Descripción"
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              <ItemNotasTextField
                                fullWidth
                                value={item.notas ?? ''}
                                maxLength={ITEM_NOTAS_MAX}
                                placeholder="Notas"
                                onChange={(notas) => updateItemProducto(idx, { notas })}
                              />
                            </TableCell>
                          <TableCell align="right" sx={{ width: 90 }}>
                            <DecimalTextField
                              value={item.cantidad ?? 1}
                              onChange={(v) => updateItemProducto(idx, { cantidad: v })}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 110 }}>
                            <DecimalTextField
                              value={item.costoBrutoUnitario ?? 0}
                              onChange={(v) => updateItemProducto(idx, { costoBrutoUnitario: v })}
                              inputProps={{ min: 0, step: 1 }}
                              sx={{ width: 120 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 80 }}>
                            <DecimalTextField
                              value={item.porcDesc1 ?? 0}
                              onChange={(v) => updateItemProducto(idx, { porcDesc1: v })}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 140, minWidth: 140 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                              <CurrencyTextField
                                value={subtotalEfectivoItem(item)}
                                onChange={(v) => updateItemProducto(idx, { subtotalManual: v })}
                                sx={{
                                  width: 120,
                                  ...(item.subtotalManual !== undefined
                                    ? { '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }
                                    : {}),
                                }}
                              />
                              {item.subtotalManual !== undefined && (
                                <Tooltip title="Usar subtotal calculado">
                                  <IconButton
                                    size="small"
                                    onClick={() => updateItemProducto(idx, { subtotalManual: undefined })}
                                    aria-label="Usar subtotal calculado"
                                  >
                                    <RestartAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => removeItemProducto(idx)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addItemProducto}
                    variant="outlined"
                    sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                  >
                    Agregar producto
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={openBuscarLineasFacturadas}
                    sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                  >
                    Buscar ítems facturados
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion expanded={accordionConceptosExpanded} onChange={(_, exp) => setAccordionConceptosExpanded(!!exp)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0, marginLeft: 0.5 },
                  '& .MuiAccordionSummary-content': { margin: 0, marginRight: 0 }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#424242' }}>Items de Gastos</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Cant.</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Neto Grav/Unit</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">IVA {impuestoTasas.impuestotasa1 ?? 21}%</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">IVA {impuestoTasas.impuestotasa2 ?? 10.5}%</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">IVA {impuestoTasas.impuestotasa3 ?? 27}%</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Exento/No Grav.</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemsConceptos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                            Sin ítems de concepto. Use el botón Agregar concepto para añadir.
                          </TableCell>
                        </TableRow>
                      ) : (
                        itemsConceptos.map((item, idx) => (
                          <TableRow key={`c-${idx}`}>
                            <TableCell>
                              <TextField
                                size="small"
                                value={item.codigoArticulo || ''}
                                placeholder="Concepto"
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setImputableSearchConceptoIndex(idx);
                                          setOpenImputableSearchConcepto(true);
                                        }}
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                                onClick={() => {
                                  setImputableSearchConceptoIndex(idx);
                                  setOpenImputableSearchConcepto(true);
                                }}
                                sx={{ minWidth: 160 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.descripcion || ''}
                                onChange={(e) => updateItemConcepto(idx, { descripcion: e.target.value })}
                                placeholder="Descripción"
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 140 }}>
                              <ItemNotasTextField
                                fullWidth
                                value={item.notas ?? ''}
                                maxLength={ITEM_NOTAS_MAX}
                                placeholder="Notas"
                                onChange={(notas) => updateItemConcepto(idx, { notas })}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 80 }}>
                              <DecimalTextField
                                value={item.cantidad ?? 1}
                                onChange={(qty) => {
                                  const netoUnit = item.costoBrutoUnitario ?? 0;
                                  const netoTotal = qty * netoUnit;
                                  const letraVal = formulario.letra?.trim().toUpperCase() || '';
                                  const porcTasa1 = impuestoTasas.impuestotasa1 ?? 21;
                                  const updates: Partial<CreateItemCompraDto> = { cantidad: qty };
                                  if (letraVal === 'A' && netoTotal > 0) {
                                    updates.tasa1 = Math.round(netoTotal * (porcTasa1 / 100) * 100) / 100;
                                    updates.tasa2 = 0;
                                    updates.tasa3 = 0;
                                    updates.tasa4 = 0;
                                    updates.tasa5 = 0;
                                  }
                                  updateItemConcepto(idx, updates);
                                }}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                sx={{ width: 70 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 110 }}>
                              <DecimalTextField
                                value={item.costoBrutoUnitario ?? 0}
                                onChange={(netoUnit) => {
                                  const qty = item.cantidad ?? 1;
                                  const netoTotal = qty * netoUnit;
                                  const letraVal = formulario.letra?.trim().toUpperCase() || '';
                                  const porcTasa1 = impuestoTasas.impuestotasa1 ?? 21;
                                  const updates: Partial<CreateItemCompraDto> = { costoBrutoUnitario: netoUnit };
                                  if (letraVal === 'A' && netoTotal > 0) {
                                    updates.tasa1 = Math.round(netoTotal * (porcTasa1 / 100) * 100) / 100;
                                    updates.tasa2 = 0;
                                    updates.tasa3 = 0;
                                    updates.tasa4 = 0;
                                    updates.tasa5 = 0;
                                  }
                                  updateItemConcepto(idx, updates);
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                                placeholder="0"
                                sx={{ width: 110 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 90 }}>
                              <DecimalTextField
                                value={item.tasa1 ?? 0}
                                onChange={(v) => updateItemConcepto(idx, { tasa1: v, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: 0 })}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 90 }}>
                              <DecimalTextField
                                value={item.tasa2 ?? 0}
                                onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: v, tasa3: 0, tasa4: 0, tasa5: 0 })}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 90 }}>
                              <DecimalTextField
                                value={item.tasa3 ?? 0}
                                onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: 0, tasa3: v, tasa4: 0, tasa5: 0 })}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 100 }}>
                              <DecimalTextField
                                value={(item.tasa4 ?? 0) + (item.tasa5 ?? 0)}
                                // En letra C cargamos "Exento/No Grav." en tasa5 (dejando tasa4 en 0).
                                onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: v })}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: 90 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => removeItemConcepto(idx)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addItemConcepto}
                    variant="outlined"
                    sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                  >
                    Agregar concepto
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={openBuscarLineasFacturadas}
                    sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                  >
                    Buscar ítems facturados
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>

          <ImputableSearch
            open={openImputableSearchConcepto}
            onClose={() => { setOpenImputableSearchConcepto(false); setImputableSearchConceptoIndex(null); }}
            onSelect={handleSelectImputableConcepto}
            tipoMovimientoIE="E"
          />

          <Typography variant="h6" sx={{ mb: 2, mt: 3, color: '#1976d2' }}>
            Totales
          </Typography>
          <Card sx={{ mt: 2, boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Subtotal Bruto:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {formatCurrency(subtotalBruto)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'grey.300', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800, color: 'text.secondary' }}>
                  Bonificación Aplicada
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <DecimalTextField
                    value={formulario.porcDesc ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc: v })}
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography>
                  <DecimalTextField
                    value={formulario.porcDesc2 ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc2: v })}
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography>
                  <DecimalTextField
                    value={formulario.porcDesc3 ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc3: v })}
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    Total Bonificación Aplicada:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                    {formatCurrency(totalBonificacion)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1, pl: 3.5, flexWrap: 'wrap', gap: 1 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {letra === 'A' ? 'Subtotal Gravado:' : 'Subtotal Neto:'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CurrencyTextField
                      value={subtotalResumenEfectivo}
                      onChange={(v) => setSubtotalManualUsuario(v)}
                      sx={{
                        width: 140,
                        ...(subtotalManualUsuario !== null
                          ? { '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }
                          : {}),
                      }}
                    />
                    {subtotalManualUsuario !== null && (
                      <Button size="small" variant="text" onClick={() => setSubtotalManualUsuario(null)}>
                        Usar calculado
                      </Button>
                    )}
                  </Box>
                </Box>
                {letra === 'C' ? (
                  <Box display="flex" justifyContent="space-between" sx={{ pl: 3.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Exento:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {formatCurrency(montoExentoLineas)}
                    </Typography>
                  </Box>
                ) : letra === 'X' ? (
                  <Box display="flex" justifyContent="space-between" sx={{ pl: 3.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      IVA No categorizado:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {formatCurrency(subtotalNeto)}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ pl: 3.5, flexWrap: 'wrap', gap: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      IVA:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CurrencyTextField
                        value={ivaEfectivo}
                        onChange={(v) => setIvaManualUsuario(v)}
                        sx={{
                          width: 140,
                          ...(ivaManualUsuario !== null
                            ? { '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }
                            : {}),
                        }}
                      />
                      {ivaManualUsuario !== null && (
                        <Button size="small" variant="text" onClick={() => setIvaManualUsuario(null)}>
                          Usar calculado
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
                <Accordion expanded={expandOtrosImpuestos} onChange={(_, exp) => setExpandOtrosImpuestos(exp)} sx={{ mt: 2, borderRadius: 1, '&:before': { display: 'none' }, boxShadow: 'none', border: 'none', bgcolor: 'transparent' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    flexDirection: 'row-reverse',
                    minHeight: 40,
                    py: 0,
                    px: 0,
                    '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0, marginLeft: 0.5 },
                    '& .MuiAccordionSummary-content': { margin: 0, marginRight: 0 }
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Otros impuestos
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
                      {formatCurrency(totalImpuestosAdicionales)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Cod. Impuesto</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Cta Ctable</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Monto Neto</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Impuesto</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {impuestosAdicionales.map((ia, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{ia.codigoImpuesto}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={ia.ctaCtable || ''}
                                  onChange={(e) => updateImpuestoAdicional(idx, { ctaCtable: e.target.value === '' ? 0 : Number(e.target.value) })}
                                  sx={{ width: 90 }}
                                  inputProps={{ style: { textAlign: 'right' } }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => { setImputableSearchImpuestoIndex(idx); setOpenImputableSearchImpuesto(true); }}
                                >
                                  <SearchIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={ia.descripcion}
                                onChange={(e) => updateImpuestoAdicional(idx, { descripcion: e.target.value })}
                              />
                            </TableCell>
                          <TableCell align="right">
                            <DecimalTextField
                              value={ia.montoNeto ?? 0}
                              onChange={(v) => updateImpuestoAdicional(idx, { montoNeto: v })}
                              sx={{ width: 110 }}
                              inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <DecimalTextField
                              value={ia.montoImpuesto ?? 0}
                              onChange={(v) => updateImpuestoAdicional(idx, { montoImpuesto: v })}
                              sx={{ width: 110, fontWeight: 600 }}
                              inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                            />
                          </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => removeImpuestoAdicional(idx)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setOpenImpuestoSearch(true)}
                    variant="outlined"
                    size="small"
                    sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                  >
                    Agregar impuesto
                  </Button>
                </AccordionDetails>
              </Accordion>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'text.secondary' }}>
                    Total Final:
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'text.secondary' }}>
                    {formatCurrency(totalFinal)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
            >
              {saving ? <CircularProgress size={24} /> : 'Guardar nota de crédito'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/compras/notas-credito')} disabled={saving}>
              Cancelar
            </Button>
          </Stack>
          <NumeroInternoMeta nro={0} />
        </CardContent>
      </Card>

      <UbicacionSearch
        open={openUbicacionSearch}
        onClose={() => setOpenUbicacionSearch(false)}
        onSelect={handleUbicacionSelect}
      />

      <ImputableSearch
        open={openImputableSearchImpuesto}
        onClose={() => { setOpenImputableSearchImpuesto(false); setImputableSearchImpuestoIndex(null); }}
        onSelect={handleImputableSelectImpuesto}
        tipoMovimientoIE="E"
      />

      <Dialog open={openImpuestoSearch} onClose={() => { setOpenImpuestoSearch(false); setImpuestoSearchTerm(''); setImpuestosSearchPage(0); }} maxWidth="sm" fullWidth>
        <DialogTitle>Buscar impuesto</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={impuestoSearchTerm}
              onChange={(e) => setImpuestoSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setImpuestosSearchPage(0), loadImpuestosSearch())}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Button variant="outlined" onClick={() => { setImpuestosSearchPage(0); loadImpuestosSearch(); }} sx={{ color: '#1976d2', borderColor: '#1976d2' }}>
              Buscar
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="center">Seleccionar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {impuestosSearchResults.map((imp) => {
                  const codigo = (imp.codigoImpuesto || imp.CodigoImpuesto || '').trim();
                  const desc = (imp.descripcion || imp.Descripcion || '-').trim();
                  const tipo = (imp.tipo || imp.Tipo || '-').trim();
                  return (
                    <TableRow key={codigo} hover>
                      <TableCell>{codigo}</TableCell>
                      <TableCell>{desc}</TableCell>
                      <TableCell>{tipo}</TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => handleSelectImpuesto(imp)}>
                          Seleccionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={impuestosSearchTotal}
            page={impuestosSearchPage}
            onPageChange={(_, p) => setImpuestosSearchPage(p)}
            rowsPerPage={10}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenImpuestoSearch(false); setImpuestoSearchTerm(''); setImpuestosSearchPage(0); }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openProductSearch} onClose={() => { setOpenProductSearch(false); setProductSearchIndex(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Buscar artículo</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setProductosPage(0), loadProductos())}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Button variant="outlined" onClick={() => { setProductosPage(0); loadProductos(); }} sx={{ color: '#1976d2', borderColor: '#1976d2' }}>
              Buscar
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">Costo</TableCell>
                  <TableCell align="center">Seleccionar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productos.map((p) => {
                  const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis);
                  const esDis = esTipoDiscontinuado(p.tipoArticulo);
                  return (
                  <TableRow key={p.codigoArticulo || p.codigo || ''} hover sx={{ opacity: disBloqueado ? 0.75 : 1 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{p.codigoArticulo || p.codigo || '-'}</span>
                        {esDis && <Chip size="small" label="DIS" color="warning" />}
                      </Stack>
                    </TableCell>
                    <TableCell>{p.descripcion || p.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(Number(p.costo) || 0)}</TableCell>
                    <TableCell align="center">
                      <Button size="small" disabled={disBloqueado} onClick={() => handleSelectProduct(p)}>
                        {disBloqueado ? 'No seleccionable' : 'Seleccionar'}
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={productosTotalCount}
            page={productosPage}
            onPageChange={(_, p) => setProductosPage(p)}
            rowsPerPage={productosRowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenProductSearch(false); setProductSearchIndex(null); }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openBuscarFacturaModal} onClose={() => setOpenBuscarFacturaModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Buscar factura de compra</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 1 }} flexWrap="wrap">
            <TextField
              size="small"
              label="Buscar"
              placeholder="Texto libre…"
              value={facturasModalSearch}
              onChange={(e) => setFacturasModalSearch(e.target.value)}
              sx={{ minWidth: 180, flex: 1 }}
            />
            <TextField
              size="small"
              label="Fecha desde"
              type="date"
              value={facturasModalFechaDesde}
              onChange={(e) => setFacturasModalFechaDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Fecha hasta"
              type="date"
              value={facturasModalFechaHasta}
              onChange={(e) => setFacturasModalFechaHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setFacturasModalPage(0);
                setFacturasModalQueryKey((k) => k + 1);
              }}
              sx={{ color: '#1976d2', borderColor: '#1976d2', alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              Buscar
            </Button>
          </Stack>
          {facturasModalLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nº formulario</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Comprobante</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturasModalList.map((fc) => (
                    <TableRow key={fc.nroFormulario} hover>
                      <TableCell>{fc.nroFormulario}</TableCell>
                      <TableCell>{formatDateLocal(fc.fechaEmision) || '—'}</TableCell>
                      <TableCell>{formatoFacturaCompraCorto(fc.letra, fc.nroSuc, fc.nroComprobante ?? undefined)}</TableCell>
                      <TableCell align="right">{formatCurrency(fc.importeTotal ?? 0)}</TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="contained" onClick={() => void seleccionarFacturaDesdeModal(fc.nroFormulario)} disabled={loadingFacturaOrigen}>
                          Seleccionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={facturasModalTotal}
            page={facturasModalPage}
            onPageChange={(_, p) => setFacturasModalPage(p)}
            rowsPerPage={facturasModalRowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[facturasModalRowsPerPage]}
            labelRowsPerPage="Filas"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBuscarFacturaModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPendientes} onClose={() => setOpenPendientes(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ítems ya facturados (desde órdenes / remitos)</DialogTitle>
        <DialogContent>
          {loadingPendientes ? (
            <CircularProgress size={22} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Comprobante origen</TableCell>
                    <TableCell>Fecha emisión</TableCell>
                    <TableCell>Artículo / concepto</TableCell>
                    <TableCell align="right">Cant. pedida</TableCell>
                    <TableCell align="right">Facturado (FC)</TableCell>
                    <TableCell align="right">Devuelto (NC)</TableCell>
                    <TableCell align="right">Disponible NC</TableCell>
                    <TableCell align="center" sx={{ width: 44 }} />
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientes.map((p) => (
                    <TableRow key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteMasterDisplay(p)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatFechaEmisionComprobanteMaster(p.fechaEmisionComprobanteMaster)}</TableCell>
                      <TableCell>{p.codigoArticulo} - {p.descripcionArticulo}</TableCell>
                      <TableCell align="right">{p.cantidadPedida}</TableCell>
                      <TableCell align="right">{p.cantidadFacturada}</TableCell>
                      <TableCell align="right">{p.cantidadDevuelta}</TableCell>
                      <TableCell align="right">{cantidadFacturadaNetaDisponible(p)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver documentos facturados y remitidos">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPendienteHiloInfo({
                                nroFormularioMaster: p.nroFormularioMaster,
                                nroItemMaster: p.nroItemMaster,
                                resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                              })
                            }
                            aria-label="Información facturado y remitido"
                          >
                            <InfoOutlinedIcon fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => void agregarPendienteProducto(p, { cantidadDesdeFacturado: true })}>
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPendientes(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CompraPendienteHiloInfoDialog
        open={!!pendienteHiloInfo}
        onClose={() => setPendienteHiloInfo(null)}
        nroFormularioMaster={pendienteHiloInfo?.nroFormularioMaster ?? null}
        nroItemMaster={pendienteHiloInfo?.nroItemMaster ?? null}
        resumenLinea={pendienteHiloInfo?.resumenLinea}
      />
    </Box>
  );
};

export default NotaCreditoCompraForm;